import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { RootState } from 'app/AppStore';
import { useDisclosure } from 'components/Dialog';
import {
  getEasyBasketOperationDetails,
  getEasyBasketSelectedDisabledReferencesMap,
  getEasyBasketSelectedOperationId,
  getEasyBasketSelectedReferencesMap,
} from 'domains/easyBasket/EasyBasket.store';
import { TAB_PARTS_SELECTION } from 'domains/easyBasket/EasyBasket.types';
import { getCurrency } from 'domains/user';
import { TotalStripDialog } from 'pages/CatalogPage/DH/SubcategorySection/OperationsSection/common/TotalStripDialog';
import {
  cleanDuplicatedPartsInGroups,
  mergeSingleReferenceDuplicatedGroups,
  useTotalReferencesPrice,
} from 'pages/CatalogPage/DH/SubcategorySection/OperationsSection/utils';
import { BlackButton, Flex, Pipeline, Text } from 'UI';
import { textFormatter } from 'utils';
import { StickyStrip } from './PartsSelection.styled';

export interface ReferencesTotalStripProps {
  onClickNext: () => void;
}

const ReferencesTotalStrip = ({ onClickNext }: ReferencesTotalStripProps) => {
  const { t } = useTranslation();
  const operationId = useSelector((state: RootState) => getEasyBasketSelectedOperationId(state));
  const operationDetails = useSelector((state: RootState) => getEasyBasketOperationDetails(state, operationId));
  const selectedReferences = useSelector(getEasyBasketSelectedReferencesMap);
  const disabledSelectedReferences = useSelector(getEasyBasketSelectedDisabledReferencesMap);
  const referenceTotalPrices = useTotalReferencesPrice();
  const currency = useSelector(getCurrency);

  const hasSomePartGroupMissingSelection = useMemo(() => {
    const partsItems = operationDetails?.data?.basketContent.partsSection.partsItems ?? [];
    const filteredDuplicatedGroupParts = cleanDuplicatedPartsInGroups(partsItems);
    const mergedDuplicatedGroups = mergeSingleReferenceDuplicatedGroups(filteredDuplicatedGroupParts);

    const unselectedGroups = mergedDuplicatedGroups?.filter((group) => !selectedReferences.has(group.itemGroup));

    if (!unselectedGroups) {
      return true;
    }
    if (unselectedGroups.length > 0) {
      return !unselectedGroups.every((group) => disabledSelectedReferences.has(group.itemGroup));
    } else {
      return false;
    }
  }, [operationDetails?.data?.basketContent.partsSection.partsItems, selectedReferences, disabledSelectedReferences]);

  const disclosure = useDisclosure();
  const { onOpen: onClickNextAttachConfirmation, onClose } = disclosure;

  const handleClickOnNext = () => {
    if (hasSomePartGroupMissingSelection) {
      onClickNextAttachConfirmation();
    } else {
      onClickNext();
    }
  };

  return (
    <StickyStrip>
      <TotalStripDialog tab={TAB_PARTS_SELECTION} disclosure={disclosure} onClose={onClose} onClickNext={onClickNext} />
      <Flex align={'center'} gap={15}>
        <Flex direction={'column'}>
          <Text type="heading4">{t('catalog.easy_basket.tab.total.references_total.title', 'References Total')}</Text>
          <Text type="base">
            {t(
              'catalog.easy_basket.tab.total.references_total.description',
              'Total price of selected references for this operation',
            )}
          </Text>
        </Flex>
        <Pipeline size={55} />
        <Flex direction={'column'} size={0} minWidth={200} align={'center'}>
          <Flex direction={'column'}>
            <Text type="lg" bold disableGutter noWrap>
              {t('common.price.vat_include_price', '{{vat_include_price}} VAT. Incl', {
                vat_include_price: textFormatter.formatCurrency(referenceTotalPrices.totalVatIncluded, currency),
              })}
            </Text>
            <Text type="sm" disableGutter noWrap>
              {t('common.price.or_vat_exclude_price', 'or {{vat_exclude_price}} VAT. Excl', {
                vat_exclude_price: textFormatter.formatCurrency(referenceTotalPrices.totalVatExcluded, currency),
              })}
            </Text>
          </Flex>
        </Flex>
        <Pipeline size={55} />
        <Flex justify={'center'} size={0} minWidth={250}>
          <BlackButton size={'large'} onClick={handleClickOnNext}>
            <Flex minWidth={150} align={'center'} justify={'center'}>
              {t('common.action.next', 'Next')}
            </Flex>
          </BlackButton>
        </Flex>
      </Flex>
    </StickyStrip>
  );
};

export default ReferencesTotalStrip;
